import React, { useEffect, useState } from "react";
import { useAtom } from "jotai/index";
import { T2SEngineAtom, currentSpeakerAtom, isProcessingAtom } from "~/Atom";
import voicevoxData from "./voicevox/speakers_info.json";
import openaiData from "./open_ai_tts/speakers_info.json";
import { getSpeakers } from "./aivisspeech/AivisSpeechUtils";
import { getNameAndStyle } from "~/Commons";
import { Info } from "shared/Schemas";
let aivisSpeechCache: Speaker[] = []; // AivisSpeechのデータをキャッシュするためのグローバル変数

// ALL | SELF_ONLY
type PermittedSynthesisMorphing = string;

type SupportedFeatures = {
  permitted_synthesis_morphing: PermittedSynthesisMorphing;
};

interface Style {
  name: string;
  id: number;
  type: string;
}

interface Speaker {
  name: string;
  styles: Style[];
  speaker_uuid?: string;
  version?: string;
  supported_features?: SupportedFeatures;
}

const getEngineData = (T2SEngine: string) => {
  switch (T2SEngine) {
    case "VOICEVOX":
      return voicevoxData;
    case "OpenAI":
      return openaiData;
    case "AivisSpeech":
      return aivisSpeechCache;
    default:
      return null;
  }
};

const findStyleInfo = (data: Speaker[], id: number): Info | null => {
  for (const item of data) {
    if (item.styles) {
      for (const style of item.styles) {
        if (style.id === id) {
          return { name: item.name, styleName: style.name, type: style.type };
        }
      }
    }
  }
  return null;
};

export const extractInfo = (T2SEngine: string, id: number): Info | null => {
  const data = getEngineData(T2SEngine);

  if (!data || data.length === 0) {
    console.error(`No data available for engine: ${T2SEngine}`);
    return null;
  }

  try {
    return findStyleInfo(data, id);
  } catch (err) {
    console.error(`Error extracting info for ${T2SEngine}, id: ${id}`, err);
    console.info("Data structure:", JSON.stringify(data).substring(0, 200) + "...");
    return null;
  }
};

const SpeakersList: React.FC = () => {
  const [currentSpeaker, setCurrentSpeaker] = useAtom(currentSpeakerAtom);
  const [isProcessing] = useAtom(isProcessingAtom);
  const [T2SEngine] = useAtom(T2SEngineAtom);
  const [data, setData] = useState<Speaker[]>([]); // データを状態管理
  // selectedEngine の変更に応じてデータを設定
  useEffect(() => {
    switch (T2SEngine) {
      case "VOICEVOX":
        setData(voicevoxData);
        break;
      case "OpenAI":
        setData(openaiData);
        break;
      case "AivisSpeech":
        // APIからデータを取得し、グローバルキャッシュと状態を更新
        // ToDo オンライン版でcustomerごとに取得するモデルを変更する改修が必要
        getSpeakers()
          .then((speakersData) => {
            // キャッシュと状態を更新
            aivisSpeechCache = speakersData;
            setData(speakersData);
          })
          .catch((error) => {
            console.error("AivisSpeech speakers fetch error:", error);
            setData([]);
          });
        break;
      default:
        setData([]);
    }
  }, [T2SEngine]); // T2SEngine が変更されるたびにデータが更新される

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = parseInt(e.target.value);
    const info = extractInfo(T2SEngine, id);
    const { name, style } = getNameAndStyle(T2SEngine, info);
    // 共通の処理
    setCurrentSpeaker({
      Id: id,
      Name: name,
      Style: style,
    });
  };

  return (
    <div className={"speakers-dropdown"} style={{ position: "relative" }}>
      <select
        value={currentSpeaker.Id.toString()}
        className={"form-select font-default-normal-20"}
        onChange={handleChange}
        disabled={isProcessing}
      >
        <option value="" disabled>
          Select a speaker
        </option>
        {data.length > 0 ? (
          data.map((speaker, index) => (
            <optgroup label={speaker.name} key={index}>
              {speaker.styles &&
                speaker.styles.map((style, styleIndex) => (
                  <option key={styleIndex} value={`${style.id}`}>
                    {speaker.name} - {style.name}
                  </option>
                ))}
            </optgroup>
          ))
        ) : (
          <option disabled>スピーカーデータを読み込み中...</option>
        )}
      </select>
      <div className="custom-arrow"></div>
    </div>
  );
};
export default SpeakersList;
